import { useState } from 'react';
import {
  MdListAlt,
  MdMoreHoriz,
  MdOutlineCurrencyExchange,
  MdPersonOutline,
  MdPersonAddAlt
} from 'react-icons/md';
import { useRouter } from 'next/router';

import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import { EventGuestBreakdown } from 'legacy/search/types';

import SubmitRefundStep from 'screens/events/EventDashboard/components/new/OptionsModal/components/SubmitRefundStep';
import { StepsIds } from 'screens/events/EventDashboard/components/new/OptionsModal/types';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import { useGetEventEntryGuestDetail } from 'legacy/events/hooks';
import { EventAnalyticsGuestsDataPointDto } from 'legacy/events/types';
import * as Toast from 'legacy/common/utils/toast';
import { useSendFriendRequest } from 'legacy/events/hooks/useFriendRequest';

type Props = {
  ticket: EventAnalyticsGuestsDataPointDto;
  eventId: string;
  onClose: () => void;
  onViewDetails: () => void;
  refetchGuestList: () => void;
  showTicketOptions: StepsIds;
  isOrganizer: boolean;
};

const OptionsModal = ({
  ticket,
  onClose,
  onViewDetails,
  eventId,
  refetchGuestList,
  isOrganizer,
  showTicketOptions = StepsIds.DefaultOptions
}: Props) => {
  const router = useRouter();

  const [refundModalOpen, setRefundModalOpen] = useState(
    showTicketOptions === StepsIds.SubmitRefundStep
  );

  const { data: guestDetail } = useGetEventEntryGuestDetail({
    eventId,
    ticketId: ticket.id
  });

  const { mutate: sendFriendRequest } = useSendFriendRequest({
    userId: guestDetail?.purchaser?.id
  });

  const goToStep = () => {
    setRefundModalOpen(false);
  };

  return (
    <Modal
      visible
      onClose={onClose}
      type={ModalType.SMALL}
      title="More Actions"
      icon={<MdMoreHoriz size={20} />}
      autoHeight
    >
      <>
        <div className="p-4 flex flex-col gap-3">
          <Button
            onClick={onViewDetails}
            type={ButtonType.Inverted}
            icon={<MdListAlt size={18} />}
          >
            View Details
          </Button>
          <Button
            onClick={() => {
              router.push(`/profile/${guestDetail?.purchaser?.id}`);
            }}
            type={ButtonType.Inverted}
            icon={<MdPersonOutline size={18} />}
          >
            View Profile
          </Button>
          {guestDetail?.user.email && (
            <Button
              onClick={() => {
                window.location.href = `mailto:${guestDetail?.user.email}`;
              }}
              type={ButtonType.Inverted}
              icon={<MdPersonOutline size={18} />}
            >
              Contact Purchaser
            </Button>
          )}

          <Button
            onClick={() =>
              sendFriendRequest(undefined, {
                onError: (error) => Toast.error(error.message),
                onSuccess: () =>
                  Toast.success('Friend request sent successfully!')
              })
            }
            type={ButtonType.Inverted}
            icon={<MdPersonAddAlt size={18} />}
          >
            Add Friend
          </Button>
        </div>
        {!refundModalOpen && (
          <div className="border-t-1 border-solid border-gray-200 p-4 mt-2">
            <Button
              onClick={() => setRefundModalOpen(true)}
              type={ButtonType.Clear}
              className="text-red-500"
              icon={<MdOutlineCurrencyExchange size={18} />}
            >
              Refund Ticket
            </Button>
          </div>
        )}
        {refundModalOpen && (
          <SubmitRefundStep
            goToStep={goToStep}
            ticket={
              {
                bundleProductId: guestDetail?.bundleProductId,
                bundleProductName: guestDetail?.bundleProductName,
                bundleInstanceId: guestDetail?.bundleInstanceId,
                user: {
                  _id: guestDetail?.user.id,
                  name: `${guestDetail?.user.firstName} ${guestDetail?.user.lastName}`,
                  firstName: guestDetail?.user.firstName,
                  lastName: guestDetail?.user.lastName,
                  photo: {
                    _id: guestDetail?.user.id,
                    url: guestDetail?.user.photoUrl
                  }
                },
                ticketId: ticket.id,
                ticketNotes: '', // missing from backend
                purchaser: {
                  id: guestDetail?.purchaser.id,
                  name: `${guestDetail?.purchaser.firstName} ${guestDetail?.purchaser.lastName}`,
                  firstName: guestDetail?.purchaser.lastName,
                  lastName: guestDetail?.purchaser.lastName,
                  photoUrl: '' // missing from backend
                },
                tierData: {
                  id: guestDetail?.tier.id,
                  name: guestDetail?.tier?.name
                }
              } as EventGuestBreakdown
            }
            onClose={onClose}
            onViewDetails={onViewDetails}
            eventId={eventId}
            refetchGuestList={refetchGuestList}
            isOrganizer={isOrganizer}
          />
        )}
      </>
    </Modal>
  );
};

export default OptionsModal;
